// react
import { useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import CookieOutlinedIcon from '@mui/icons-material/CookieOutlined';

// material ui
import { Box, Button, Paper, Typography } from '@mui/material';

const COOKIE_CONSENT_KEY = 'cookie-consent';
const COOKIE_CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;

export type CookieConsent = 'analytics' | 'essential';

type CookieConsentRecord = {
  consent: CookieConsent;
  updatedAt: string;
};

const getStoredCookieConsent = (): CookieConsentRecord | null => {
  const storedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);

  if (!storedConsent) {
    return null;
  }

  try {
    const parsedConsent = JSON.parse(storedConsent) as CookieConsentRecord;

    if (
      parsedConsent?.consent === 'analytics' ||
      parsedConsent?.consent === 'essential'
    ) {
      return parsedConsent;
    }
  } catch {
    if (storedConsent === 'analytics' || storedConsent === 'essential') {
      return {
        consent: storedConsent,
        updatedAt: new Date(0).toISOString(),
      };
    }
  }

  return null;
};

const isCookieConsentExpired = (updatedAt: string) => {
  const updatedTime = Date.parse(updatedAt);

  if (Number.isNaN(updatedTime)) {
    return true;
  }

  return Date.now() - updatedTime >= COOKIE_CONSENT_MAX_AGE_MS;
};

interface CookiePreferenceModalProps {
  onConsent?: (consent: CookieConsent) => void;
  openRequestId?: number | null;
}

const CookiePreferenceModal = ({
  onConsent,
  openRequestId,
}: CookiePreferenceModalProps) => {
  const { t } = useTranslation('app');
  const [visible, setVisible] = useState(() => {
    if (openRequestId != null) {
      return true;
    }

    const storedConsent = getStoredCookieConsent();

    return !storedConsent || isCookieConsentExpired(storedConsent.updatedAt);
  });

  const handleConsent = (consent: CookieConsent) => {
    localStorage.setItem(
      COOKIE_CONSENT_KEY,
      JSON.stringify({
        consent,
        updatedAt: new Date().toISOString(),
      }),
    );
    setVisible(false);
    onConsent?.(consent);
  };

  if (!visible) return null;

  return (
    <Paper
      elevation={6}
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        maxWidth: 300,
        p: 2.5,
        borderRadius: 2,
        zIndex: (theme) => theme.zIndex.snackbar,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      {' '}
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 1.5,
        }}
      >
        <CookieOutlinedIcon
          sx={{ color: 'primary.contrastText', fontSize: 22 }}
        />
      </Box>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.75 }}>
        {t('cookiePreferences.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('cookiePreferences.description')}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Button
          variant="outlined"
          size="small"
          fullWidth
          onClick={() => handleConsent('analytics')}
          sx={{ textTransform: 'none' }}
          data-testid="cookie-preference-allow-analytics-button"
        >
          {t('cookiePreferences.allowAnalytics')}
        </Button>
        <Button
          variant="outlined"
          size="small"
          fullWidth
          onClick={() => handleConsent('essential')}
          sx={{ textTransform: 'none' }}
          data-testid="cookie-preference-essential-only-button"
        >
          {t('cookiePreferences.essentialOnly')}
        </Button>
      </Box>
    </Paper>
  );
};

export default CookiePreferenceModal;
